import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { QueryConfig } from '@/lib/tanstack-query';
import { Membership } from '@/types/api/membership';
import z from 'zod';

const GetMembershipIncludeSchema = z.enum(['group', 'user']);

export const GetMembershipParamsSchema = z.object({
  include: z.array(GetMembershipIncludeSchema).optional(),
});

export type GetMembershipParams = z.infer<typeof GetMembershipParamsSchema>;

export const getMembership = async ({
  userId,
  groupId,
  params,
}: {
  userId: string;
  groupId: string;
  params?: GetMembershipParams;
}): Promise<Membership> => {
  return (await api.get(`/orgs/${groupId}/memberships/${userId}`, { params })).data;
};

export const getMembershipQueryOptions = (userId: string, groupId: string, params?: GetMembershipParams) => {
  return queryOptions({
    queryKey: ['orgs', groupId, 'memberships', userId, params],
    queryFn: () => getMembership({ userId, groupId, params }),
    enabled: Boolean(userId) && Boolean(groupId),
  });
};

type UseMembershipOptions = {
  userId: string;
  groupId: string;
  params?: GetMembershipParams;
  queryConfig?: QueryConfig<typeof getMembershipQueryOptions>;
};

export const useMembership = ({ userId, groupId, queryConfig, params }: UseMembershipOptions) => {
  return useQuery({
    ...getMembershipQueryOptions(userId, groupId, params),
    ...queryConfig,
  });
};

export const useMembershipSuspense = ({ userId, groupId, queryConfig, params }: UseMembershipOptions) => {
  return useSuspenseQuery({
    ...getMembershipQueryOptions(userId, groupId, params),
    ...queryConfig,
  });
};
